import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormLabel,
	Grid,
	IconButton,
	LinearProgress,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { MuiFileInput } from 'mui-file-input';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';

import {
	useDeleteRequestQuestionnaireFormMutation,
	useGetRequestQuestionnaireQuery,
	useGetRequestQuestionnaireFormsQuery,
	useGenerateQuestionnaireTokenMutation,
	useLazyGetRequestQuestionnaireFormQuery,
	useSubmitRequestQuestionnaireMutation,
	useUploadRequestQuestionnaireFormMutation,
} from '../../slices/apis/requestApi';
import { QuestionnaireStatus } from '../questionnaire';
import { Request, type AnswerRow, type QuestionOption } from '../../models';
import { useRole } from '../../hooks/useRole';
import { fDateTime } from '../../utils/formatTime';
import { getFileMime } from '../../utils/config';
import toDataUri from '../../utils/toDataUri';
import Iconify from '../iconify';

interface QuestionnaireProps {
	requestId: string;
}

interface QuestionnaireFormData {
	file: File[];
}

function EditYesNo({
	row,
	value,
	onChange,
	disabled = false,
}: {
	row: AnswerRow;
	value: string;
	onChange: (v: string) => void;
	disabled?: boolean;
}) {
	return (
		<FormControl component="fieldset">
			<FormLabel component="legend" sx={{ mb: 0.5, fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
				{row.snapshotText}
			</FormLabel>
			<RadioGroup row value={value} onChange={(e) => onChange(e.target.value)}>
				<FormControlLabel value="YES" control={<Radio size="small" disabled={disabled} />} label="Yes" />
				<FormControlLabel value="NO" control={<Radio size="small" disabled={disabled} />} label="No" />
			</RadioGroup>
		</FormControl>
	);
}

function EditSingleChoice({
	row,
	value,
	onChange,
	disabled = false,
}: {
	row: AnswerRow;
	value: string;
	onChange: (v: string) => void;
	disabled?: boolean;
}) {
	return (
		<FormControl component="fieldset">
			<FormLabel component="legend" sx={{ mb: 0.5, fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
				{row.snapshotText}
			</FormLabel>
			<RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
				{(row.snapshotOptions ?? []).map((opt: QuestionOption) => (
					<FormControlLabel
						key={opt.label}
						value={opt.label}
						control={<Radio size="small" disabled={disabled} />}
						label={opt.label}
					/>
				))}
			</RadioGroup>
		</FormControl>
	);
}

function EditMultiChoice({
	row,
	value,
	onChange,
	disabled = false,
}: { row: AnswerRow; value: string[]; onChange: (v: string[]) => void; disabled?: boolean }) {
	const handleChange = (label: string, checked: boolean) => {
		if (disabled) {
			return;
		}

		const opt = (row.snapshotOptions ?? []).find((o: QuestionOption) => o.label === label);
		if (checked) {
			if (opt?.exclusive) {
				onChange([label]);
			} else {
				const exclusiveLabels = (row.snapshotOptions ?? [])
					.filter((o: QuestionOption) => o.exclusive)
					.map((o: QuestionOption) => o.label);
				onChange([...value.filter((v) => !exclusiveLabels.includes(v)), label]);
			}
		} else {
			onChange(value.filter((v) => v !== label));
		}
	};

	return (
		<FormControl component="fieldset">
			<FormLabel component="legend" sx={{ mb: 0.5, fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
				{row.snapshotText}
			</FormLabel>
			<FormGroup>
				{(row.snapshotOptions ?? []).map((opt: QuestionOption) => (
					<FormControlLabel
						key={opt.label}
						control={
							<Checkbox
								size="small"
								disabled={disabled}
								checked={value.includes(opt.label)}
								onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(opt.label, e.target.checked)}
							/>
						}
						label={opt.label}
					/>
				))}
			</FormGroup>
		</FormControl>
	);
}

function EditText({
	row,
	value,
	onChange,
	disabled = false,
}: {
	row: AnswerRow;
	value: string;
	onChange: (v: string) => void;
	disabled?: boolean;
}) {
	return (
		<TextField
			label={row.snapshotText}
			multiline
			minRows={2}
			fullWidth
			size="small"
			disabled={disabled}
			value={value}
			onChange={(e) => onChange(e.target.value)}
		/>
	);
}

function ConsentDisplay({ content }: { content?: string | null }) {
	if (!content) {
		return null;
	}
	return (
		<Box
			sx={{
				fontSize: '0.9rem',
				lineHeight: 1.7,
				color: 'text.primary',
				'& ol, & ul': { pl: 3, mb: 1.5 },
				'& li': { mb: 1 },
				'& p': { mb: 1 },
				'& em': { fontStyle: 'italic' },
				'& strong, & b': { fontWeight: 600 },
			}}
			// biome-ignore lint/security/noDangerouslySetInnerHtml: consent HTML is trusted server-authored content
			dangerouslySetInnerHTML={{ __html: content }}
		/>
	);
}

export function Questionnaire({ requestId }: QuestionnaireProps) {
	const { isAdmin } = useRole();
	const { data, isLoading, isError } = useGetRequestQuestionnaireQuery(requestId);
	const { data: questionnaireForms = [], isFetching: isFetchingQuestionnaireForms } =
		useGetRequestQuestionnaireFormsQuery(requestId);
	const [generateToken, { isLoading: isGenerating }] = useGenerateQuestionnaireTokenMutation();
	const [submitAnswers, { isLoading: isSaving }] = useSubmitRequestQuestionnaireMutation();
	const [uploadQuestionnaireForm, { isLoading: isUploadingQuestionnaireForm, isSuccess: isUploadQuestionnaireFormSuccess }] =
		useUploadRequestQuestionnaireFormMutation();
	const [deleteQuestionnaireForm, { isLoading: isDeletingQuestionnaireForm, isSuccess: isDeleteQuestionnaireFormSuccess }] =
		useDeleteRequestQuestionnaireFormMutation();
	const [triggerQuestionnaireForm, { data: questionnaireFormData, isFetching: isFetchingQuestionnaireForm }] =
		useLazyGetRequestQuestionnaireFormQuery();

	const [dialogOpen, setDialogOpen] = useState(false);
	const [questionnaireFormDeleteConfirmDialogOpen, setQuestionnaireFormDeleteConfirmDialogOpen] = useState(false);

	const [publicLinkCopied, setPublicLinkCopied] = useState(false);
	const [emailSent, setEmailSent] = useState(false);
	const [selectedQuestionnaireForm, setSelectedQuestionnaireForm] = useState<string | null>(null);
	const [questionnaireFormTabOpened, setQuestionnaireFormTabOpened] = useState(false);

	const [editMode, setEditMode] = useState(false);
	const [editAnswers, setEditAnswers] = useState<Record<number, string | string[]>>({});
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saveSuccess, setSaveSuccess] = useState(false);
	const canManageQuestionnaireForms = isAdmin;

	const {
		control,
		reset,
		handleSubmit,
	} = useForm<QuestionnaireFormData>({
		defaultValues: {
			file: [],
		},
	});

	useEffect(() => {
		if (isUploadQuestionnaireFormSuccess) {
			reset({ file: [] });
		}

		if (isDeleteQuestionnaireFormSuccess) {
			setQuestionnaireFormDeleteConfirmDialogOpen(false);
		}
	}, [isUploadQuestionnaireFormSuccess, isDeleteQuestionnaireFormSuccess, reset]);

	const handleFormUpload: SubmitHandler<QuestionnaireFormData> = async (formData) => {
		if (!canManageQuestionnaireForms) {
			return;
		}

		const files = formData.file ?? [];

		if (files.length === 0) {
			return;
		}

		const forms = (
			await Promise.all(
				files.map(async (file) => {
					const dataUri = await toDataUri(file);

					if (!dataUri) {
						return null;
					}

					const base64Encoded = dataUri.split(',')[1];

					return {
						data: base64Encoded,
						name: file.name,
						type: file.type,
					};
				}),
			)
		).filter((form) => form !== null);

		if (forms.length === 0) {
			return;
		}

		uploadQuestionnaireForm({
			requestId,
			forms,
		});
	};

	const onDeleteQuestionnaireFormConfirmDialogClose = () => {
		setQuestionnaireFormDeleteConfirmDialogOpen(false);
	};

	const onQuestionnaireFormDownload = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			setQuestionnaireFormTabOpened(false);
			triggerQuestionnaireForm({
				requestId: Number(requestId),
				formName: e.currentTarget.getAttribute('data-form') as string,
				action: 'download',
			});
		},
		[requestId, triggerQuestionnaireForm],
	);

	const onQuestionnaireFormPreview = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			setQuestionnaireFormTabOpened(false);
			triggerQuestionnaireForm({
				requestId: Number(requestId),
				formName: e.currentTarget.getAttribute('data-form') as string,
			});
		},
		[requestId, triggerQuestionnaireForm],
	);

	const onQuestionnaireFormDeleteClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		if (!canManageQuestionnaireForms) {
			return;
		}

		setQuestionnaireFormDeleteConfirmDialogOpen(true);
		setSelectedQuestionnaireForm(e.currentTarget.getAttribute('data-form') as string);
	}, [canManageQuestionnaireForms]);

	const onQuestionnaireFormDeleteConfirm = () => {
		if (!canManageQuestionnaireForms) {
			return;
		}

		deleteQuestionnaireForm({
			requestId,
			formName: selectedQuestionnaireForm,
		});
	};

	const handleGenerateToken = async () => {
		setEmailSent(false);
		await generateToken(requestId).unwrap();
		setEmailSent(true);
	};

	const handleCopyPublicLink = () => {
		if (!data?.questionnaireUrl) return;
		navigator.clipboard.writeText(data.questionnaireUrl);
		setPublicLinkCopied(true);
		setTimeout(() => setPublicLinkCopied(false), 2000);
	};

	const handleEditStart = () => {
		const initial: Record<number, string | string[]> = {};
		if (data) {
			for (const section of data.sections) {
				for (const a of section.answers) {
					// Skip CONSENT answers — they are never editable; also guards against
					// old rows where snapshotType was stored before the CONSENT enum migration
					if (a.questionId !== null && a.snapshotType !== 'CONSENT' && a.snapshotText !== 'Privacy Consent Form') {
						initial[a.questionId] = a.answer;
					}
				}
			}
		}
		setEditAnswers(initial);
		setSaveError(null);
		setSaveSuccess(false);
		setEditMode(true);
	};

	const handleEditCancel = () => {
		setEditMode(false);
		setSaveError(null);
	};

	const handleEditSave = async () => {
		setSaveError(null);
		setSaveSuccess(false);
		try {
			await submitAnswers({
				requestId,
				body: {
					answers: Object.entries(editAnswers).map(([id, answer]) => ({
						questionId: Number(id),
						answer,
					})),
				},
			}).unwrap();
			setSaveSuccess(true);
			setEditMode(false);
		} catch (err: unknown) {
			const e = err as { data?: { error?: string } | string; status?: number };
			const msg = typeof e.data === 'object' ? (e.data?.error ?? 'Save failed.') : (e.data ?? 'Save failed.');
			setSaveError(msg);
		}
	};

	const handleCloseDialog = () => {
		if (editMode) {
			setEditMode(false);
			setSaveError(null);
		}
		setDialogOpen(false);
	};

	const handleOpenDialog = () => {
		setDialogOpen(true);
	};

	const setAnswer = (questionId: number, value: string | string[]) => {
		setEditAnswers((prev) => ({ ...prev, [questionId]: value }));
	};

	const renderAnswerField = (answer: AnswerRow, value: string | string[], disabled = false) => {
		if (answer.snapshotType === 'YES_NO') {
			return (
				<EditYesNo
					row={answer}
					value={typeof value === 'string' ? value : ''}
					onChange={(v) => answer.questionId !== null && setAnswer(answer.questionId, v)}
					disabled={disabled}
				/>
			);
		}

		if (answer.snapshotType === 'MULTIPLE_CHOICE' && answer.snapshotSelectionType === 'SINGLE') {
			return (
				<EditSingleChoice
					row={answer}
					value={typeof value === 'string' ? value : ''}
					onChange={(v) => answer.questionId !== null && setAnswer(answer.questionId, v)}
					disabled={disabled}
				/>
			);
		}

		if (answer.snapshotType === 'MULTIPLE_CHOICE' && answer.snapshotSelectionType === 'MULTI') {
			return (
				<EditMultiChoice
					row={answer}
					value={Array.isArray(value) ? value : []}
					onChange={(v) => answer.questionId !== null && setAnswer(answer.questionId, v)}
					disabled={disabled}
				/>
			);
		}

		if (answer.snapshotType === 'TEXT') {
			return (
				<EditText
					row={answer}
					value={typeof value === 'string' ? value : ''}
					onChange={(v) => answer.questionId !== null && setAnswer(answer.questionId, v)}
					disabled={disabled}
				/>
			);
		}

		return null;
	};

	const isSubmitted = !!data?.submittedAt;
	const hasSubmission = data?.submissionId != null;
	const statusValue = isSubmitted ? 'submitted' : 'pending';
	// View is available to any role once a submission exists — even before the
	// candidate fills it — showing the full questionnaire template (incl. consent).
	const showViewAction = !isLoading && !isError && hasSubmission;
	// The questionnaire link/email flow is admin-only.
	const canGenerateQuestionnaireLink = isAdmin;

	// Link area is admin-only: hint, Link field (shown whenever a token/URL exists,
	// i.e. from Created through Submitted), and the Email button.
	// `topDivider` adds a separator when content precedes it (the submitted state).
	const renderLinkSection = (topDivider: boolean) => {
		if (!isAdmin) {
			return null;
		}
		return (
			<Box
				sx={{
					width: '100%',
					...(topDivider ? { pt: 2, borderTop: '1px solid', borderColor: 'divider' } : {}),
				}}
			>
				<Stack spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
					<Typography variant="body2" color="text.secondary">
						Email a new questionnaire link to candidate. The link is valid for 7 days.
					</Typography>
					{data?.questionnaireUrl && (
						<Box sx={{ width: '100%' }}>
							<Stack
								direction={{ xs: 'column', sm: 'row' }}
								spacing={1}
								alignItems={{ xs: 'stretch', sm: 'center' }}
								sx={{ width: '100%' }}
							>
								<Box
									sx={{
										flex: 1,
										minWidth: 0,
										py: 0.75,
										px: 1.25,
										borderRadius: 1,
										bgcolor: 'grey.100',
										border: '1px solid',
										borderColor: 'divider',
									}}
								>
									<Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
										{data.questionnaireUrl}
									</Typography>
								</Box>
								<Button
									size="small"
									variant="outlined"
									startIcon={<ContentCopyIcon fontSize="small" />}
									onClick={handleCopyPublicLink}
									sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
								>
									{publicLinkCopied ? 'Copied!' : 'Copy'}
								</Button>
							</Stack>
						</Box>
					)}
					{emailSent && (
						<Alert severity="success" icon={<EmailIcon fontSize="inherit" />} sx={{ width: '100%' }}>
							A questionnaire link has been sent to the candidate's email.
						</Alert>
					)}
					<Button
						variant="contained"
						size="small"
						sx={{ textTransform: 'none' }}
						onClick={handleGenerateToken}
						disabled={!canGenerateQuestionnaireLink || isGenerating}
						startIcon={isGenerating ? <CircularProgress size={14} color="inherit" /> : null}
					>
						New Questionnaire Link
					</Button>
				</Stack>
			</Box>
		);
	};

	const renderFormsSection = () => {
		if (!canManageQuestionnaireForms && questionnaireForms.length === 0) {
			return null;
		}

		if (questionnaireFormData?.uri && !questionnaireFormTabOpened) {
			setQuestionnaireFormTabOpened(true);
			window.open(questionnaireFormData.uri, '_blank');
		}

		return (
			<Box
				sx={{
					width: '100%',
					pt: 2,
					borderTop: '1px solid',
					borderColor: 'divider',
				}}
			>
			<Stack spacing={1.5} sx={{ width: '100%' }}>
				{(isFetchingQuestionnaireForms || isFetchingQuestionnaireForm || isUploadingQuestionnaireForm) && (
					<LinearProgress />
				)}
					{!isFetchingQuestionnaireForms && questionnaireForms.length > 0 ? (
						<Stack spacing={1.5}>
							{questionnaireForms.map((form) => (
								<Grid container key={form.Key}>
									<Grid size={{ md: 10, xs: 10 }}>
										<Typography
											variant="body1"
											sx={{
												':hover': {
													cursor: 'pointer',
													color: (theme) => theme.palette.primary.main,
												},
											}}
											data-form={Request.getReportName(form.Key) ?? ''}
											onClick={onQuestionnaireFormPreview}
										>
											{Request.getReportName(form.Key)}
										</Typography>
									</Grid>
									<Grid size={{ md: 2, xs: 2 }}>
										<Stack direction="row" spacing={0.5} justifyContent="flex-end">
											<IconButton
												edge="end"
												aria-label="download"
												data-form={Request.getReportName(form.Key) ?? ''}
												onClick={onQuestionnaireFormDownload}
											>
												<Iconify
													icon={'eva:cloud-download-outline'}
													sx={{
													color: (theme) => theme.palette.primary.main,
												}}
											/>
										</IconButton>
										{canManageQuestionnaireForms && (
											<IconButton
												edge="end"
												aria-label="delete"
												data-form={Request.getReportName(form.Key) ?? ''}
												onClick={onQuestionnaireFormDeleteClick}
											>
												<Iconify
													icon={'eva:trash-2-outline'}
													sx={{
														color: (theme) => theme.palette.error.main,
													}}
												/>
											</IconButton>
										)}
										</Stack>
									</Grid>
								</Grid>
							))}
						</Stack>
					) : null}
					{canManageQuestionnaireForms && (
						<Box>
						<form onSubmit={handleSubmit(handleFormUpload)}>
							<Controller
								control={control}
								name="file"
								render={({ field: { onChange, value }, fieldState }) => (
									<MuiFileInput
										multiple
										value={value}
										onChange={onChange}
										placeholder="Select your questionnaire form"
										helperText={fieldState.invalid ? 'File is invalid' : ''}
										error={fieldState.invalid}
										InputProps={{
											inputProps: {
												accept: getFileMime(),
											},
											startAdornment: <Iconify icon={'iconamoon:attachment-light'} />,
										}}
										clearIconButtonProps={{
											title: 'Remove',
											children: <Iconify icon={'iconamoon:close-bold'} />,
										}}
									/>
								)}
							/>
							<Box>
								<Button type="submit" variant="contained" sx={{ mt: 2 }}>
									Upload
								</Button>
							</Box>
						</form>
						</Box>
					)}
				</Stack>
			</Box>
		);
	};

	return (
		<>
			<Card variant="outlined">
				<CardHeader
					title={
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
							<Typography variant="h6">Questionnaire</Typography>
							<QuestionnaireStatus status={statusValue} />
						</Box>
					}
					sx={{ color: 'grey.800' }}
					action={
						showViewAction && (
							<Button variant="contained" size="small" onClick={handleOpenDialog}>
								View
							</Button>
						)
					}
				/>
				<CardContent>
					{isSubmitted && !isLoading && !isError && (
						<Stack spacing={2} alignItems="flex-start">
							<Stack direction="row" spacing={0.5} alignItems="baseline">
								<Typography variant="subtitle1">Submitted:</Typography>
								<Typography variant="body1">{fDateTime(data.submittedAt)}</Typography>
							</Stack>
							{data?.consentGivenAt && (
								<Stack direction="row" spacing={0.5} alignItems="baseline">
									<Typography variant="subtitle1">Consent given:</Typography>
									<Typography variant="body1">{fDateTime(data.consentGivenAt)}</Typography>
								</Stack>
							)}
							{renderLinkSection(true)}
							{renderFormsSection()}
						</Stack>
					)}
					{!isSubmitted && !isLoading && !isError && (
						<Stack spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
							{renderLinkSection(false) ?? (
								<Typography variant="body2" color="text.secondary">
									The questionnaire has not been submitted yet.
								</Typography>
							)}
							{renderFormsSection()}
						</Stack>
					)}
				</CardContent>
			</Card>

			<Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth scroll="paper">
				<DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
					{!editMode && isAdmin && data && data.sections.length > 0 && (
						<Button size="medium" startIcon={<EditIcon />} onClick={handleEditStart}>
							Edit
						</Button>
					)}
				</DialogTitle>
				<IconButton
					size="small"
					onClick={handleCloseDialog}
					sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
				<DialogContent sx={{ position: 'relative' }}>
					<Stack spacing={2}>
						{saveSuccess && <Alert severity="success">Questionnaire updated successfully!</Alert>}

						{/* ── Edit mode (admin only) ── */}
						{editMode && isAdmin && data && (
							<Stack spacing={3}>
								{data.sections.map((section) => (
									<Box key={section.sectionName ?? 'unknown'}>
										<Typography variant="subtitle2" gutterBottom>
											{section.sectionName ?? 'General'}
										</Typography>
										<Divider sx={{ mb: 1.5 }} />
										<Stack spacing={2} pl={1}>
											{section.answers.map((a, idx) => {
												// Consent is shown read-only — it is the candidate's to give, not editable here.
												if (a.snapshotType === 'CONSENT') {
													return <ConsentDisplay key={a.questionId ?? idx} content={a.consentContent} />;
												}
												if (a.questionId === null) {
													return null;
												}
												const qid = a.questionId;
												return <Box key={qid}>{renderAnswerField(a, editAnswers[qid])}</Box>;
											})}
										</Stack>
									</Box>
								))}
								{saveError && <Alert severity="error">{saveError}</Alert>}
								<Stack direction="row" spacing={1}>
									<Button
										loading={isSaving}
										variant="contained"
										size="small"
										onClick={handleEditSave}
										disabled={isSaving}
									>
										<span>Save</span>
									</Button>
									<Button variant="outlined" size="small" onClick={handleEditCancel} disabled={isSaving}>
										Cancel
									</Button>
								</Stack>
							</Stack>
						)}

						{/* ── Read-only view (full template, incl. consent) ── */}
						{!editMode && data && data.sections.length > 0 && (
							<Stack spacing={2}>
								{data.sections.map((section) => (
									<Box key={section.sectionName ?? 'unknown'}>
										<Typography variant="subtitle2" gutterBottom>
											{section.sectionName ?? 'General'}
										</Typography>
										<Divider sx={{ mb: 1.5 }} />
										<Stack spacing={2} pl={1}>
											{section.answers.map((a, idx) =>
												a.snapshotType === 'CONSENT' ? (
													<ConsentDisplay key={a.questionId ?? idx} content={a.consentContent} />
												) : (
													<Box key={a.questionId ?? idx}>{renderAnswerField(a, a.answer, true)}</Box>
												),
											)}
										</Stack>
									</Box>
								))}
							</Stack>
						)}

						{!editMode && (!data || data.sections.length === 0) && (
							<Typography variant="body2" color="text.secondary">
								The questionnaire has not been submitted yet.
							</Typography>
						)}
					</Stack>
				</DialogContent>
			</Dialog>

			<Dialog
				open={Boolean(questionnaireFormDeleteConfirmDialogOpen)}
				onClose={onDeleteQuestionnaireFormConfirmDialogClose}
				title={'Warning'}
			>
				<Grid container spacing={2} sx={{ p: 2, justifyContent: 'center', alignItems: 'center' }}>
					<Grid size={{ md: 12 }}>
						<Typography variant="subtitle2">{`Confirm to delete form ${selectedQuestionnaireForm}`}</Typography>
					</Grid>
					<Grid size={{ md: 4 }}>
						<Button
							loading={isDeletingQuestionnaireForm}
							variant="outlined"
							color="primary"
							onClick={onQuestionnaireFormDeleteConfirm}
						>
							<span>Confirm</span>
						</Button>
					</Grid>
					<Grid size={{ md: 4 }}>
						<Button color="error" variant="outlined" onClick={onDeleteQuestionnaireFormConfirmDialogClose}>
							Cancel
						</Button>
					</Grid>
				</Grid>
			</Dialog>
		</>
	);
}
